import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axiosClient from '../../api/axiosClient';

// Icons
const Icons = {
  Clock: () => <span className="text-lg">⏰</span>,
  Plus: () => <span className="text-lg">➕</span>,
  Trash: () => <span className="text-lg">🗑️</span>,
  Copy: () => <span className="text-lg">📋</span>,
  Save: () => <span className="text-lg">💾</span>
};

const Availability = () => {
  const { isDark } = useTheme();
  const { user } = useAdminAuth();
  const { t, i18n } = useTranslation('admin');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [barbers, setBarbers] = useState([]);
  const [selectedBarberId, setSelectedBarberId] = useState('');
  const [originalWeek, setOriginalWeek] = useState(null);
  const [weekSchedule, setWeekSchedule] = useState({
    mon: [],
    tue: [],
    wed: [],
    thu: [],
    fri: [],
    sat: [],
    sun: []
  });
  const [shopTimezone, setShopTimezone] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [exceptions, setExceptions] = useState([]);
  const [loadingExceptions, setLoadingExceptions] = useState(false);
  const [showExceptionDialog, setShowExceptionDialog] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);

  const dayNames = {
    mon: t('admin.weekday_monday'),
    tue: t('admin.weekday_tuesday'),
    wed: t('admin.weekday_wednesday'),
    thu: t('admin.weekday_thursday'),
    fri: t('admin.weekday_friday'),
    sat: t('admin.weekday_saturday'),
    sun: t('admin.weekday_sunday')
  };

  // Auto-select barber for barber role
  useEffect(() => {
    if (user?.role === 'barber' && user?.id && barbers.length > 0) {
      const barberExists = barbers.find(b => b.id === user.id);
      if (barberExists) {
        setSelectedBarberId(user.id.toString());
      }
    }
  }, [user, barbers]);

  const loadBarbers = useCallback(async () => {
    try {
      const response = await axiosClient.get('/admin/barbers?status=active&pageSize=100');
      setBarbers(response.data.items || []);
    } catch (error) {
      console.error('Error loading barbers:', error);
      setError(t('admin.error_load_barbers'));
    }
  }, [t]);

  const loadAvailability = useCallback(async (barberId) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axiosClient.get(`/admin/availability?barber_id=${barberId}`);
      const data = response.data;
      
      setWeekSchedule(data.week);
      setOriginalWeek(JSON.stringify(data.week));
      setShopTimezone(data.shop_tz || '');
      
    } catch (error) {
      console.error('Error loading availability:', error);
      if (error.response?.status === 404) {
        // No availability set yet, start with empty schedule
        const emptyWeek = {
          mon: [],
          tue: [],
          wed: [],
          thu: [],
          fri: [],
          sat: [],
          sun: []
        };
        setWeekSchedule(emptyWeek);
        setOriginalWeek(JSON.stringify(emptyWeek));
      } else {
        setError(t('admin.error_load_availability'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadExceptions = useCallback(async (barberId) => {
    setLoadingExceptions(true);
    
    try {
      // Load exceptions for next 60 days
      const fromDate = new Date().toISOString().split('T')[0];
      const toDate = new Date();
      toDate.setDate(toDate.getDate() + 60);
      const toDateStr = toDate.toISOString().split('T')[0];
      
      const response = await axiosClient.get(`/admin/availability-exceptions?barber_id=${barberId}&from=${fromDate}&to=${toDateStr}`);
      setExceptions(response.data.exceptions || []);
      
    } catch (error) {
      console.error('Error loading exceptions:', error);
      if (error.response?.status !== 404) {
        setError(t('admin.error_load_availability_exceptions'));
      }
    } finally {
      setLoadingExceptions(false);
    }
  }, [t]);

  // Load barbers on component mount
  useEffect(() => {
    loadBarbers();
  }, [loadBarbers]);

  // Load availability when barber is selected
  useEffect(() => {
    if (selectedBarberId) {
      loadAvailability(selectedBarberId);
      loadExceptions(selectedBarberId);
    }
  }, [selectedBarberId, loadAvailability, loadExceptions]);

  const handleDeleteException = async (exceptionId) => {
    setConfirmData({
      type: 'delete',
      title: t('admin.delete_exception_title'),
      message: t('admin.delete_exception_message'),
      warning: t('admin.delete_exception_warning'),
      onConfirm: () => performDeleteException(exceptionId)
    });
    setShowConfirmModal(true);
  };

  const performDeleteException = async (exceptionId) => {

    try {
      await axiosClient.delete(`/admin/availability-exceptions/${exceptionId}`);
      
      // Reload exceptions
      if (selectedBarberId) {
        loadExceptions(selectedBarberId);
      }
      
      setSuccessMessage(t('admin.exception_deleted_success'));
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error deleting exception:', error);
      setError(t('admin.error_delete_exception'));
    }
  };

  const handleCreateException = async (exceptionData) => {
    try {
      await axiosClient.post('/admin/availability-exceptions', {
        barber_id: selectedBarberId,
        ...exceptionData
      });
      
      // Reload exceptions
      if (selectedBarberId) {
        loadExceptions(selectedBarberId);
      }
      
      setShowExceptionDialog(false);
      setSuccessMessage(t('admin.exception_created_success'));
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error creating exception:', error);
      throw error; // Re-throw for dialog to handle
    }
  };

  const hasChanges = useCallback(() => {
    return originalWeek !== JSON.stringify(weekSchedule);
  }, [originalWeek, weekSchedule]);

  const addInterval = (day) => {
    setWeekSchedule(prev => ({
      ...prev,
      [day]: [
        ...prev[day],
        { start_local: '09:00', end_local: '17:00' }
      ]
    }));
    setError('');
    setSuccessMessage('');
  };

  const removeInterval = (day, index) => {
    setWeekSchedule(prev => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index)
    }));
    setError('');
    setSuccessMessage('');
  };

  const updateInterval = (day, index, field, value) => {
    // Validate time format
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (value && !timeRegex.test(value)) {
      return; // Don't update if invalid format
    }

    setWeekSchedule(prev => ({
      ...prev,
      [day]: prev[day].map((interval, i) => 
        i === index 
          ? { ...interval, [field]: value }
          : interval
      )
    }));
    setError('');
    setSuccessMessage('');
  };

  const copyDayToOthers = (sourceDay) => {
    const sourceIntervals = weekSchedule[sourceDay];
    const newWeek = { ...weekSchedule };
    
    Object.keys(dayNames).forEach(day => {
      if (day !== sourceDay) {
        newWeek[day] = sourceIntervals.map(interval => ({ ...interval }));
      }
    });
    
    setWeekSchedule(newWeek);
    setError('');
    setSuccessMessage('');
  };

  const validateSchedule = () => {
    for (const [day, intervals] of Object.entries(weekSchedule)) {
      for (let i = 0; i < intervals.length; i++) {
        const interval = intervals[i];
        
        // Check time format
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(interval.start_local) || !timeRegex.test(interval.end_local)) {
          return `${dayNames[day]}: Invalid time format in interval ${i + 1}`;
        }

        // Check 5-minute granularity
        const [startHour, startMin] = interval.start_local.split(':').map(Number);
        const [endHour, endMin] = interval.end_local.split(':').map(Number);
        
        if (startMin % 5 !== 0 || endMin % 5 !== 0) {
          return `${dayNames[day]}: Times must be in 5-minute increments (interval ${i + 1})`;
        }

        // Check start < end
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        
        if (startMinutes >= endMinutes) {
          return `${dayNames[day]}: Start time must be before end time (interval ${i + 1})`;
        }
      }

      // Check for overlaps
      const sortedIntervals = intervals
        .map((interval, index) => ({ ...interval, originalIndex: index }))
        .sort((a, b) => a.start_local.localeCompare(b.start_local));

      for (let i = 0; i < sortedIntervals.length - 1; i++) {
        const current = sortedIntervals[i];
        const next = sortedIntervals[i + 1];
        
        if (current.end_local > next.start_local) {
          return `${dayNames[day]}: Overlapping time intervals detected`;
        }
      }
    }
    
    return null;
  };

  const handleSave = async () => {
    if (!selectedBarberId) {
      setError(t('admin.error_select_barber'));
      return;
    }

    const validationError = validateSchedule();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    setSuccessMessage('');

    try {
      await axiosClient.post('/admin/availability', {
        barber_id: selectedBarberId,
        week: weekSchedule
      });

      setOriginalWeek(JSON.stringify(weekSchedule));
      setSuccessMessage(t('admin.availability_updated_success'));
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (error) {
      console.error('Error saving availability:', error);
      
      if (error.response?.data?.error) {
        setError(error.response.data.message || t('admin.error_save_availability'));
      } else {
        setError(t('admin.error_save_availability'));
      }
    } finally {
      setSaving(false);
    }
  };

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 5) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${timeStr}`).toLocaleTimeString(i18n.language || 'en', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        options.push({ value: timeStr, label: displayTime });
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  return (
    <div className={`p-6 ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold flex items-center">
              <Icons.Clock />
              <span className="ml-2">{t('admin.availability')}</span>
            </h1>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('admin.availability_subtitle')}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className={`text-sm px-3 py-2 rounded-lg ${
              isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-50 text-blue-800'
            }`}>
              {t('admin.all_times_shown_in', { timezone: shopTimezone })}
            </div>
            
            <button
              onClick={handleSave}
              disabled={!hasChanges() || saving || !selectedBarberId}
              className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center ${
                !hasChanges() || saving || !selectedBarberId
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDark 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              <Icons.Save />
              <span className="ml-2">
                {saving ? t('admin.saving') : t('admin.save_changes')}
              </span>
            </button>
          </div>
        </div>

        {/* Barber Selection */}
        {user?.role !== 'barber' && (
          <div className="mb-6">
            <label className={`block text-sm font-medium mb-2 ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {t('admin.select_barber')}
            </label>
            <select
              value={selectedBarberId}
              onChange={(e) => setSelectedBarberId(e.target.value)}
              className={`px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark 
                  ? 'bg-gray-800 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            >
              <option value="">{t('admin.select_barber_placeholder')}</option>
              {barbers.map(barber => (
                <option key={barber.id} value={barber.id}>
                  {barber.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {/* Weekly Schedule */}
        {selectedBarberId && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Weekly Template */}
            <div className="lg:col-span-2">
          <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <div className="p-6">
                {Object.entries(dayNames).map(([dayKey, dayName]) => (
                  <div key={dayKey} className={`py-4 border-b last:border-b-0 ${
                    isDark ? 'border-gray-700' : 'border-gray-200'
                  }`}>
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-lg font-medium">{dayName}</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => addInterval(dayKey)}
                          className={`px-3 py-1 rounded text-sm flex items-center ${
                            isDark 
                              ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                              : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                          }`}
                        >
                          <Icons.Plus />
                          <span className="ml-1">{t('admin.add')}</span>
                        </button>
                        {weekSchedule[dayKey].length > 0 && (
                          <button
                            onClick={() => copyDayToOthers(dayKey)}
                            className={`px-3 py-1 rounded text-sm flex items-center ${
                              isDark 
                                ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                            }`}
                          >
                            <Icons.Copy />
                            <span className="ml-1">{t('admin.copy_to_all')}</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {weekSchedule[dayKey].length === 0 ? (
                      <div className={`text-center py-4 ${
                        isDark ? 'text-gray-500' : 'text-gray-400'
                      }`}>
                        {t('admin.no_availability_set')}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {weekSchedule[dayKey].map((interval, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <select
                              value={interval.start_local}
                              onChange={(e) => updateInterval(dayKey, index, 'start_local', e.target.value)}
                              className={`px-2 py-1 border rounded text-sm ${
                                isDark 
                                  ? 'bg-gray-700 border-gray-600 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            >
                              {timeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            
                            <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>{t('admin.to_connector')}</span>
                            
                            <select
                              value={interval.end_local}
                              onChange={(e) => updateInterval(dayKey, index, 'end_local', e.target.value)}
                              className={`px-2 py-1 border rounded text-sm ${
                                isDark 
                                  ? 'bg-gray-700 border-gray-600 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              }`}
                            >
                              {timeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            
                            <button
                              onClick={() => removeInterval(dayKey, index)}
                              className={`p-1 rounded text-red-600 hover:bg-red-100 ${
                                isDark ? 'hover:bg-red-900' : ''
                              }`}
                              title={t('admin.remove_interval')}
                            >
                              <Icons.Trash />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
            </div>

            {/* Exceptions Panel */}
            <div className="lg:col-span-1">
              <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium">{t('admin.date_exceptions')}</h3>
                    <button
                      onClick={() => setShowExceptionDialog(true)}
                      className={`px-3 py-1 rounded text-sm flex items-center ${
                        isDark 
                          ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                          : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
                      }`}
                    >
                      <Icons.Plus />
                      <span className="ml-1">{t('admin.add_exception')}</span>
                    </button>
                  </div>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('admin.availability_exceptions_subtitle')}
                  </p>
                </div>

                <div className="p-4">
                  {loadingExceptions ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : exceptions.length === 0 ? (
                    <div className={`text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      <p>{t('admin.no_exceptions')}</p>
                      <p className="text-xs mt-1">{t('admin.no_exceptions_hint')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {exceptions.map((exception) => (
                        <div key={exception.id} className={`p-3 rounded border ${
                          isDark ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                        }`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium">
                                {new Date(exception.date + 'T00:00:00').toLocaleDateString(i18n.language || 'en', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </div>
                              {exception.closed ? (
                                <div className={`text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                                  {t('admin.closed_all_day')}
                                </div>
                              ) : (
                                <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                  {exception.intervals.map((interval, idx) => (
                                    <div key={idx}>
                                      {new Date(`2000-01-01T${interval.start_local}`).toLocaleTimeString(i18n.language || 'en', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })} - {new Date(`2000-01-01T${interval.end_local}`).toLocaleTimeString(i18n.language || 'en', {
                                        hour: 'numeric',
                                        minute: '2-digit',
                                        hour12: true
                                      })}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => handleDeleteException(exception.id)}
                              className={`p-1 rounded text-red-600 hover:bg-red-100 ${
                                isDark ? 'hover:bg-red-900' : ''
                              }`}
                              title={t('admin.delete_exception')}
                            >
                              <Icons.Trash />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Exception Dialog - Simple Modal */}
        {showExceptionDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`p-4 sm:p-6 rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
              <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('admin.add_date_exception')}
              </h3>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const date = formData.get('date');
                const closed = formData.get('closed') === 'on';
                const startTime = formData.get('start_time');
                const endTime = formData.get('end_time');
                
                if (!date) {
                  setErrorMessage(t('admin.error_select_date'));
                  setShowErrorModal(true);
                  return;
                }
                
                const exceptionData = { date, closed };
                
                if (!closed) {
                  if (!startTime || !endTime) {
                    setErrorMessage(t('admin.error_set_start_end_times'));
                    setShowErrorModal(true);
                    return;
                  }
                  exceptionData.intervals = [{ start_local: startTime, end_local: endTime }];
                }
                
                handleCreateException(exceptionData).catch(error => {
                  if (error.response?.data?.message) {
                    setErrorMessage(error.response.data.message);
                  } else {
                    setErrorMessage(t('admin.error_create_exception'));
                  }
                  setShowErrorModal(true);
                });
              }}>
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {t('admin.date')}
                    </label>
                    <input
                      type="date"
                      name="date"
                      min={new Date().toISOString().split('T')[0]}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isDark 
                          ? 'bg-gray-700 border-gray-600 text-white' 
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="closed"
                        className="mr-2"
                        onChange={(e) => {
                          const timeInputs = e.target.form.querySelectorAll('input[type="time"]');
                          timeInputs.forEach(input => input.disabled = e.target.checked);
                        }}
                      />
                      <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                        {t('admin.closed_all_day')}
                      </span>
                    </label>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t('admin.start_time')}
                      </label>
                      <input
                        type="time"
                        name="start_time"
                        step="300"
                        defaultValue="09:00"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {t('admin.end_time')}
                      </label>
                      <input
                        type="time"
                        name="end_time"
                        step="300"
                        defaultValue="17:00"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          isDark 
                            ? 'bg-gray-700 border-gray-600 text-white' 
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowExceptionDialog(false)}
                    className={`px-4 py-2 rounded ${
                      isDark 
                        ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                    }`}
                  >
                    {t('admin.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                  >
                    {t('admin.add_exception')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && confirmData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`p-4 sm:p-6 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
            }`}>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium">
                    {confirmData.title}
                  </h3>
                </div>
              </div>
              
              <div className="mb-4">
                <p className="text-sm mb-3">
                  {confirmData.message}
                </p>
                {confirmData.warning && (
                  <p className="text-sm text-orange-600 dark:text-orange-400 mt-3 font-medium">
                    {confirmData.warning}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className={`px-4 py-2 rounded ${
                    isDark 
                      ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  {t('admin.cancel')}
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    confirmData.onConfirm();
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                >
                  {t('admin.confirm')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`p-4 sm:p-6 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto ${
              isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
            }`}>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium">
                    {t('admin.error')}
                  </h3>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm">
                  {errorMessage}
                </p>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowErrorModal(false)}
                  className={`px-4 py-2 rounded ${
                    isDark 
                      ? 'bg-gray-600 hover:bg-gray-700 text-white' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
                >
                  {t('admin.ok')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Availability;
